import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { format, startOfDay, endOfDay, parseISO } from "date-fns";
import type { DateRange } from "react-day-picker";
import {
  FileText,
  Calendar as CalendarIcon,
  Plus,
  Settings2,
  Pencil,
  Trash2,
} from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { SearchInput } from "@/components/SearchInput";
import { MeetingNoteTagPicker } from "@/components/project-detail/meeting-notes/MeetingNoteTagPicker";
import { useAllMeetingNotes } from "@/hooks/useAllMeetingNotes";
import {
  useCreateMeetingNote,
  useMeetingNoteTags,
  useUpdateMeetingNoteTag,
  useDeleteMeetingNoteTag,
  type MeetingNoteTag,
} from "@/hooks/useMeetingNotes";

export default function MeetingNotesPage() {
  const navigate = useNavigate();
  const { data: notes = [], isLoading } = useAllMeetingNotes();
  const { data: tags = [] } = useMeetingNoteTags();
  const createMeetingNote = useCreateMeetingNote();
  const updateTag = useUpdateMeetingNoteTag();
  const deleteTag = useDeleteMeetingNoteTag();
  const [search, setSearch] = useState("");
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<DateRange | undefined>(undefined);
  const [manageTagsOpen, setManageTagsOpen] = useState(false);
  const [editingTag, setEditingTag] = useState<MeetingNoteTag | null>(null);
  const [editName, setEditName] = useState("");
  const [editColor, setEditColor] = useState("");
  const [deleteConfirmTag, setDeleteConfirmTag] =
    useState<MeetingNoteTag | null>(null);

  const filteredNotes = useMemo(() => {
    let result = notes;

    // Text search filter
    if (search.trim()) {
      const lower = search.toLowerCase();
      result = result.filter(
        (note) =>
          note.title.toLowerCase().includes(lower) ||
          note.project?.name?.toLowerCase().includes(lower) ||
          note.project_group?.name?.toLowerCase().includes(lower) ||
          note.tags.some((t) => t.name.toLowerCase().includes(lower)),
      );
    }

    // Tag filter
    if (selectedTagIds.length > 0) {
      result = result.filter((note) =>
        note.tags.some((t) => selectedTagIds.includes(t.id)),
      );
    }

    // Date range filter
    if (dateRange?.from) {
      const rangeStart = startOfDay(dateRange.from);
      const rangeEnd = dateRange.to
        ? endOfDay(dateRange.to)
        : endOfDay(dateRange.from);

      result = result.filter((note) => {
        const meetingDate = parseISO(note.meeting_date);
        return meetingDate >= rangeStart && meetingDate <= rangeEnd;
      });
    }

    return result;
  }, [notes, search, selectedTagIds, dateRange]);

  const TAG_COLORS = [
    "#ef4444",
    "#f97316",
    "#eab308",
    "#22c55e",
    "#06b6d4",
    "#3b82f6",
    "#8b5cf6",
    "#ec4899",
  ];

  const openEditTag = (tag: MeetingNoteTag) => {
    setEditingTag(tag);
    setEditName(tag.name);
    setEditColor(tag.color || "#3b82f6");
  };

  const handleSaveTag = () => {
    if (!editingTag || !editName.trim()) return;
    updateTag.mutate(
      { id: editingTag.id, name: editName.trim(), color: editColor },
      { onSuccess: () => setEditingTag(null) },
    );
  };

  const handleDeleteTag = () => {
    if (!deleteConfirmTag) return;
    deleteTag.mutate(deleteConfirmTag.id, {
      onSuccess: () => {
        setDeleteConfirmTag(null);
        setSelectedTagIds((ids) =>
          ids.filter((id) => id !== deleteConfirmTag.id),
        );
      },
    });
  };

  const handleCreateNote = () => {
    createMeetingNote.mutate(
      {
        title: "Untitled Meeting Note",
        meeting_date: format(new Date(), "yyyy-MM-dd"),
      },
      {
        onSuccess: (note) => {
          navigate(`/meeting-notes/${note.id}?edit=1`);
        },
      },
    );
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Meeting Notes">
        <Button
          offlineEdit
          onClick={handleCreateNote}
          disabled={createMeetingNote.isPending}
        >
          <Plus className="h-4 w-4 mr-2" />
          {createMeetingNote.isPending ? "Creating..." : "New Meeting Note"}
        </Button>
      </PageHeader>

      <div
        className="flex flex-col sm:flex-row gap-3"
        data-testid="meeting-notes-filters"
      >
        <div className="flex-1">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Search notes..."
          />
        </div>
        <div className="flex gap-1 w-full sm:w-auto">
          <MeetingNoteTagPicker
            tags={tags}
            selectedTagIds={selectedTagIds}
            onChange={setSelectedTagIds}
          />
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 shrink-0"
            onClick={() => setManageTagsOpen(true)}
            title="Manage tags"
          >
            <Settings2 className="h-3.5 w-3.5" />
          </Button>
        </div>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              data-testid="meeting-notes-date-range"
              className={cn(
                "w-full sm:w-[240px] justify-start text-left font-normal",
                !dateRange && "text-muted-foreground",
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {dateRange?.from ? (
                dateRange.to ? (
                  <>
                    {format(dateRange.from, "dd.MM.yyyy")} –{" "}
                    {format(dateRange.to, "dd.MM.yyyy")}
                  </>
                ) : (
                  format(dateRange.from, "dd.MM.yyyy")
                )
              ) : (
                "Pick date range"
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <CalendarComponent
              mode="range"
              selected={dateRange}
              onSelect={setDateRange}
              numberOfMonths={2}
            />
          </PopoverContent>
        </Popover>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : filteredNotes.length === 0 ? (
        <Card data-testid="meeting-notes-empty">
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <FileText className="h-12 w-12 text-muted-foreground/50 mb-4" />
            <CardTitle className="text-lg mb-1">
              {search || selectedTagIds.length > 0 || !!dateRange
                ? "No notes found"
                : "No meeting notes yet"}
            </CardTitle>
            <p className="text-muted-foreground text-sm max-w-sm">
              {search || selectedTagIds.length > 0 || !!dateRange
                ? "Try adjusting your filters."
                : "Create your first meeting note to track discussions and decisions."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3" data-testid="meeting-notes-list">
          {filteredNotes.map((note) => (
            <Link
              key={note.id}
              to={`/meeting-notes/${note.id}`}
              className="block text-inherit no-underline"
            >
              <Card
                data-testid="meeting-note-card"
                className="cursor-pointer hover:bg-muted/30 transition-colors"
              >
                <CardHeader className="py-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="space-y-1 min-w-0">
                      <h3 className="font-semibold truncate">{note.title}</h3>
                      <div className="flex items-center gap-3 text-sm text-muted-foreground flex-wrap">
                        <span className="flex items-center gap-1">
                          <CalendarIcon className="h-3 w-3" />
                          {format(new Date(note.meeting_date), "dd.MM.yyyy")}
                        </span>
                        {note.project && (
                          <Badge variant="outline" className="font-normal">
                            {note.project.name}
                          </Badge>
                        )}
                        {note.project_group && !note.project && (
                          <Badge variant="outline" className="font-normal">
                            {note.project_group.name}
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1 flex-wrap shrink-0">
                      {note.tags.slice(0, 3).map((tag) => (
                        <Badge
                          key={tag.id}
                          variant="secondary"
                          style={{ backgroundColor: tag.color || undefined }}
                          className={
                            tag.color ? "text-white text-xs" : "text-xs"
                          }
                        >
                          {tag.name}
                        </Badge>
                      ))}
                      {note.tags.length > 3 && (
                        <Badge variant="secondary" className="text-xs">
                          +{note.tags.length - 3}
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardHeader>
              </Card>
            </Link>
          ))}
        </div>
      )}

      {/* Manage Tags Dialog */}
      <Dialog open={manageTagsOpen} onOpenChange={setManageTagsOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Manage Tags</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {tags.length === 0 ? (
              <p className="text-sm text-muted-foreground py-4 text-center">
                No tags yet.
              </p>
            ) : (
              tags.map((tag) => (
                <div
                  key={tag.id}
                  className="flex items-center gap-2 rounded-md border px-3 py-2"
                >
                  <div
                    className="h-3 w-3 rounded-full shrink-0"
                    style={{ backgroundColor: tag.color || "#888" }}
                  />
                  <span className="text-sm flex-1 truncate">{tag.name}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 shrink-0"
                    onClick={() => openEditTag(tag)}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 shrink-0 text-destructive hover:text-destructive"
                    onClick={() => setDeleteConfirmTag(tag)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Tag Dialog */}
      <Dialog
        open={!!editingTag}
        onOpenChange={(open) => !open && setEditingTag(null)}
      >
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Edit Tag</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="tag-name">Name</Label>
              <Input
                id="tag-name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Color</Label>
              <div className="flex gap-2 flex-wrap">
                {TAG_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={cn(
                      "h-7 w-7 rounded-full border-2 transition-transform",
                      editColor === color
                        ? "border-foreground scale-110"
                        : "border-transparent",
                    )}
                    style={{ backgroundColor: color }}
                    onClick={() => setEditColor(color)}
                  />
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditingTag(null)}>
                Cancel
              </Button>
              <Button
                offlineEdit
                onClick={handleSaveTag}
                disabled={!editName.trim() || updateTag.isPending}
              >
                {updateTag.isPending ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Tag Confirmation */}
      <AlertDialog
        open={!!deleteConfirmTag}
        onOpenChange={(open) => !open && setDeleteConfirmTag(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete tag?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the tag "{deleteConfirmTag?.name}" from all
              meeting notes. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTag}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteTag.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
